import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Music, Subtitles, Video, User, Tv, Monitor, Headphones, Globe, Star, Camera, Film, Play, Mic } from 'lucide-react';
import { heroSlides as defaultHeroSlides, features as defaultFeatures } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  MessageCircle: MessageCircle,
  Music: Music,
  Subtitles: Subtitles,
  Video: Video,
  User: User,
  Tv: Tv,
  Monitor: Monitor,
  Headphones: Headphones,
  Globe: Globe,
  Star: Star,
  Camera: Camera,
  Film: Film,
  Play: Play,
  Mic: Mic
};

const HeroCarousel = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [heroSlides, setHeroSlides] = useState([]);
  const [features, setFeatures] = useState(defaultFeatures);
  const [cinemaSession, setCinemaSession] = useState(null);

  // Check cinema session on mount
  useEffect(() => {
    const session = localStorage.getItem('cinema_session');
    if (session) {
      try {
        const parsed = JSON.parse(session);
        setCinemaSession(parsed);
      } catch (e) {
        setCinemaSession(null);
      }
    }
  }, []);

  // Load hero slides and features from backend
  useEffect(() => {
    // Load slides
    fetch(`${API_URL}/api/hero-slides/active`)
      .then(res => res.ok ? res.json() : [])
      .then(data => {
        if (data.length > 0) {
          // Map backend format to frontend format
          const mappedSlides = data.map(s => ({
            id: s.id,
            opera: s.opera,
            composer: s.composer,
            subtitle: s.subtitle,
            tagline: s.tagline,
            availability_text: s.availability_text,
            image: s.image,
            cta: s.cta,
            contentId: s.content_id,
            buttons: s.buttons || [
              { id: '1', label: 'CONTRATAR', type: 'primary', action: 'contract', is_active: true, order: 0 },
              { id: '2', label: 'VER FICHA', type: 'secondary', action: 'content', is_active: true, order: 1 }
            ]
          }));
          setHeroSlides(mappedSlides);
        }
      })
      .catch(() => {});

    // Load features
    fetch(`${API_URL}/api/hero-features/active`)
      .then(res => res.ok ? res.json() : [])
      .then(data => {
        if (data.length > 0) {
          setFeatures(data);
        }
      })
      .catch(() => {});
  }, []);

  const goToSlide = useCallback((index) => {
    if (isTransitioning || heroSlides.length === 0) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 700);
  }, [isTransitioning, heroSlides.length]);

  useEffect(() => {
    if (heroSlides.length === 0) return;
    const timer = setInterval(() => {
      goToSlide((currentSlide + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [currentSlide, goToSlide, heroSlides.length]);

  if (heroSlides.length === 0) {
    return <div className="h-screen bg-black" />;
  }

  const slide = heroSlides[currentSlide];

  const handleContentClick = () => {
    if (slide.contentId) {
      navigate(`/content/${slide.contentId}`);
    }
  };

  // Handle "CONTRATAR" button click
  const handleContratarClick = () => {
    const contentId = slide.contentId;
    
    // Check if user is logged in as an approved cinema
    if (cinemaSession && cinemaSession.status === 'approved') {
      // Redirect to cinema booking page with the content pre-selected
      if (contentId) {
        navigate(`/cinema/booking?content=${contentId}`);
      } else {
        navigate('/cinema/booking');
      }
    } else {
      // Not logged in or not approved - redirect to registration
      navigate('/cinema/register');
    }
  };

  return (
    <section className="relative h-screen w-full overflow-hidden bg-black">
      {heroSlides.map((s, index) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-700 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}
        >
          <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${s.image})` }} />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
        </div>
      ))}

      <div className="relative z-10 h-full flex flex-col justify-center px-6 md:px-12 lg:px-24 max-w-[1920px] mx-auto">
        <div className="flex gap-2 mb-6">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-1 rounded-full transition-all duration-300 ${index === currentSlide ? 'w-8 bg-[#e4002b]' : 'w-8 bg-white/30 hover:bg-white/50'}`}
            />
          ))}
        </div>

        <div className="max-w-3xl">
          <p className="text-white/80 text-sm md:text-base tracking-[0.2em] mb-4 uppercase italic">
            {slide.tagline || 'CONTRATA VERSIÓN DIGITAL'}
          </p>
          <h1 
            className="text-white text-3xl md:text-5xl lg:text-6xl font-bold tracking-wide mb-2 uppercase cursor-pointer hover:text-[#e4002b] transition-colors"
            onClick={handleContentClick}
            style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.5)' }}
          >
            {slide.opera}
          </h1>
          <h2 
            className="text-white text-xl md:text-3xl lg:text-4xl font-bold tracking-wide mb-4 uppercase"
            style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.5)' }}
          >
            {slide.subtitle}
          </h2>
          <p className="text-white/90 text-lg md:text-xl tracking-wide mb-8 uppercase">
            {slide.availability_text || 'DISPONIBLE PARA CONTRATACIÓN EN CINES'}
          </p>

          {features.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-10">
              {features.map((feature) => {
                const Icon = iconMap[feature.icon] || Video;
                return (
                  <div key={feature.id} className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-white/80" />
                    </div>
                    <span className="text-white/80 text-xs md:text-sm">{feature.label}</span>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex flex-wrap gap-4">
            {(slide.buttons || [])
              .filter(btn => btn.is_active !== false)
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((btn, idx) => {
                const handleClick = () => {
                  if (btn.action === 'contract') {
                    handleContratarClick();
                  } else if (btn.action === 'content') {
                    handleContentClick();
                  } else if (btn.action === 'link' && btn.link) {
                    // Check if external link
                    if (btn.link.startsWith('http')) {
                      window.open(btn.link, '_blank');
                    } else {
                      navigate(btn.link);
                    }
                  }
                };

                const isPrimary = btn.type === 'primary';
                return (
                  <button
                    key={btn.id || idx}
                    onClick={handleClick}
                    data-testid={`hero-btn-${btn.label?.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`px-8 py-3 text-sm font-medium tracking-wide rounded transition-colors ${
                      isPrimary
                        ? 'bg-[#e4002b] text-white hover:bg-[#c70024]'
                        : 'bg-white/10 border border-white/30 text-white hover:bg-white/20 backdrop-blur-sm'
                    }`}
                  >
                    {btn.label}
                  </button>
                );
              })}
            {/* Fallback if no buttons configured */}
            {(!slide.buttons || slide.buttons.length === 0) && (
              <>
                <button 
                  onClick={handleContratarClick}
                  data-testid="hero-contratar-btn"
                  className="px-8 py-3 bg-[#e4002b] text-white text-sm font-medium tracking-wide rounded hover:bg-[#c70024] transition-colors"
                >
                  CONTRATAR
                </button>
                <button 
                  onClick={handleContentClick}
                  data-testid="hero-ver-ficha-btn"
                  className="px-8 py-3 bg-white/10 border border-white/30 text-white text-sm font-medium tracking-wide rounded hover:bg-white/20 transition-colors backdrop-blur-sm"
                >
                  VER FICHA
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-white/50 rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default HeroCarousel;
